import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { SingleMTTWidgetDO } from 'src/components/visionx/incidents/response-widget/components/SingleMTTWidget';
import { emptyMTTRByPriority } from 'src/components/visionx/incidents/response-widget/response-widget.constants';
import { MttaByPriorityDO } from 'src/components/visionx/incidents/response-widget/types';
import { IncidentsChartsDataDO } from 'src/components/visionx/incidents/types';
import { ScreenSizeDO } from 'src/ui/screen-size-observer/screenSizeObserverRxjs.ts';

export interface ToastPosition {
  position:
    | 'top-start'
    | 'top-center'
    | 'top-end'
    | 'middle-start'
    | 'middle-center'
    | 'middle-end'
    | 'bottom-start'
    | 'bottom-center'
    | 'bottom-end';
}

export interface ToastDO {
  toastLevel: string;
  toastTitle: string;
  toastBody: string;
  showToast: boolean;
  position?: ToastPosition;
}

export interface MTTRWidgetPayload {
  widget: SingleMTTWidgetDO;
}

// Define a type for the slice state
export interface UiState {
  toast: ToastDO;
  openedRowId: string | undefined;
  isRowOpen: boolean;
  charts: { [index: string]: { [index: string]: string } };
  sideNavOpen: boolean;
  mobileNavOpen: boolean;
  currentAdminPageNewFormRoute: string | undefined;
  activeActionId: string | undefined;
  screenSize: ScreenSizeDO;
  isMobile: boolean;
  MTTRWidgets: Record<string, SingleMTTWidgetDO>;
  MTTRWidgetDateRangeLabel: string;
  MTTAByPriority: MttaByPriorityDO;
  attackStageData: IncidentsChartsDataDO['threatCategory'];
  theme: string;
}

const SIDE_NAV_OPEN_KEY: string = 'sideNavOpen';
// Define the initial state using that type
const initialState: UiState = {
  toast: {
    toastLevel: '',
    toastTitle: '',
    toastBody: '',
    showToast: false,
  },
  openedRowId: '',
  isRowOpen: false,
  charts: {},
  sideNavOpen: sessionStorage.getItem(SIDE_NAV_OPEN_KEY) === 'true' || true,
  mobileNavOpen: false,
  currentAdminPageNewFormRoute: undefined,
  activeActionId: undefined,
  screenSize: {
    width: window.innerWidth,
    height: window.innerHeight,
  },
  isMobile: window.innerWidth < 992,
  MTTRWidgets: {},
  MTTRWidgetDateRangeLabel: '',
  MTTAByPriority: emptyMTTRByPriority,
  attackStageData: {},
  theme: localStorage.getItem('theme') || 'dark',
};

export const uiSlice = createSlice({
  name: 'uiSlice',
  // `createSlice` will infer the state type from the `initialState` argument
  initialState,
  reducers: {
    setToast(state, action: PayloadAction<ToastDO>) {
      state.toast = action.payload;
    },
    closeToast(state) {
      state.toast = initialState.toast;
    },

    setOpenRowId(state, action: PayloadAction<string | undefined>) {
      state.openedRowId = action.payload;
    },
    setIsRowOpen(state, action: PayloadAction<boolean>) {
      state.isRowOpen = action.payload;
    },
    storeSingleMTTRWidget(state, action: PayloadAction<MTTRWidgetPayload>) {
      const widget = action.payload.widget;
      const key = widget.code;
      state.MTTRWidgets[key] = widget;
    },
    setSideNavOn(state) {
      sessionStorage.setItem(SIDE_NAV_OPEN_KEY, 'true');
      state.sideNavOpen = true;
    },
    setSideNavOff(state) {
      sessionStorage.setItem(SIDE_NAV_OPEN_KEY, 'false');
      state.sideNavOpen = false;
    },
    toggleMobileNav(state) {
      state.mobileNavOpen = !state.mobileNavOpen;
    },
    setCurrentAdminPageNewFormRoute(state, action: PayloadAction<string | undefined>) {
      state.currentAdminPageNewFormRoute = action.payload;
    },
    setActiveIconId(state, action: PayloadAction<string | undefined>) {
      state.activeActionId = action.payload;
    },
    setScreenSizeAndIsMobile(state, action: PayloadAction<ScreenSizeDO>) {
      const screenSize: ScreenSizeDO = action.payload;

      state.screenSize = screenSize;
      state.isMobile = screenSize.width < 992;
    },
    setDefaultMTTRDateRange(state, action: PayloadAction<string>) {
      state.MTTRWidgetDateRangeLabel = action.payload;
    },
    updateMttaByPriority(state, action: PayloadAction<MttaByPriorityDO>) {
      state.MTTAByPriority = action.payload;
    },
    setAttackStageData(state, action: PayloadAction<IncidentsChartsDataDO['threatCategory']>) {
      state.attackStageData = action.payload;
    },
    setTheme(state, action: PayloadAction<string>) {
      state.theme = action.payload;
    },
  },
});

export const {
  setToast,
  closeToast,
  setOpenRowId,
  setIsRowOpen,
  toggleMobileNav,
  setCurrentAdminPageNewFormRoute,
  setActiveIconId,
  setScreenSizeAndIsMobile,
  storeSingleMTTRWidget,
  setDefaultMTTRDateRange,
  updateMttaByPriority,
  setAttackStageData,
  setSideNavOn,
  setSideNavOff,
  setTheme,
} = uiSlice.actions;

export default uiSlice.reducer;
